import type { ConfigType } from '@plone/registry';
import { composeSchema } from '@plone/volto/helpers/Extensions';
import { findStyleByName } from '@plone/volto/helpers/Blocks/Blocks';
import ProjectgroupView from '../components/Blocks/Projectgroup/View';
import ProjectGroupEdit from '../components/Blocks/Projectgroup/Edit';
import projectGroupBlockSchema from '../components/Blocks/Projectgroup/schema';
import LatestNewsSchema from '../components/Blocks/LatestNews/schema';
import LatestNewsView from '../components/Blocks/LatestNews/View';
import PersonMetadataView from '../components/Blocks/PersonMetadata/View';
import PersonMetadataSchema from '../components/Blocks/PersonMetadata/schema';
import gridView from '../components/Blocks/Grid/View';
import { noGutterSchemaEnhancer } from '../components/Blocks/Grid/schema';
import ContactView from '../components/Blocks/Contact/View';
import FieldImageView from '../components/Blocks/FieldImage/View';
import FieldImageEdit from '../components/Blocks/FieldImage/Edit';
import fieldImageBlockSchema, {
  standAloneFieldImageBlockSchemaEnhancer,
} from '../components/Blocks/FieldImage/schema';
import descriptionSVG from '@plone/volto/icons/description.svg';
import groupSVG from '@plone/volto/icons/group.svg';
import emailSVG from '@plone/volto/icons/email.svg';
import newsSVG from '@plone/volto/icons/news.svg';
import paymentSVG from '@plone/volto/icons/payment.svg';
import TeaserBlockSAW from '../components/Blocks/Teaser/TeaserBlockSAW';
import PageHeaderView from '../components/Blocks/PageHeader/View';
import PageHeaderEdit from '../components/Blocks/PageHeader/Edit';
import CustomImageBodyTemplate from '../components/Blocks/PageHeader/CustomImageBodyTemplate';
import OnlyTitleBodyTemplate from '../components/Blocks/PageHeader/OnlyTitleBodyTemplate';
import EventHeaderTemplate from '../components/Blocks/PageHeader/EventHeaderTemplate';
import PersonBodyTemplate from '../components/Blocks/PageHeader/PersonBodyTemplate';
import ProjectBodyTemplate from '../components/Blocks/PageHeader/ProjectBodyTemplate';
import EventMetadataView from '../components/Blocks/EventMetadata/View';
import {
  PageHeaderSchema,
  pageheaderSchemaEnhancer,
} from '../components/Blocks/PageHeader/schema';
import { PageHeaderBlockDataAdapter } from '../components/Blocks/PageHeader/adapter';
import { defaultStylingSchema } from '@kitconcept/volto-light-theme/components/Blocks/schema';
import { blockWidthStylingSchema } from '../components/Blocks/schema';
import RightColumnFacets from '@plone/volto/components/manage/Blocks/Search/layout/RightColumnFacets';
import LeftColumnFacets from '@plone/volto/components/manage/Blocks/Search/layout/LeftColumnFacets';
import TopSideFacets from '@plone/volto/components/manage/Blocks/Search/layout/TopSideFacets';
import TextBlockView from 'volto-saw-homepage/customizations/volto-slate/blocks/Text/TextBlockView';
import { standAloneImageBlockSchemaEnhancer } from '@kitconcept/volto-light-theme/components/Blocks/Image/schema';
import { ImageBlockDataAdapter } from '../components/Blocks/Image/adapter';
import { imageBlockSchemaEnhancerSAW } from '../components/Blocks/Image/schema';
import SponsorView from '../components/Blocks/Sponsors/View';
import SponsorEdit from '../components/Blocks/Sponsors/Edit';
import SponsorBlockSchema from '../components/Blocks/Sponsors/schema';

export default function install(config: ConfigType) {
  config.blocks.themes = [
    {
      style: {
        '--theme-color': '#fff',
        '--theme-high-contrast-color': '#f2f7ed', // Cards in Grid block
        '--theme-foreground-color': '#000',
        '--theme-low-contrast-foreground-color': '#555555',
      },
      name: 'default',
      label: 'Default',
    },
    {
      style: {
        '--theme-color': '#f2f7ed',
        '--theme-high-contrast-color': '#fff',
        '--theme-foreground-color': '#000',
        '--theme-low-contrast-foreground-color': '#555555',
      },
      name: 'saw-green',
      label: 'Green',
    },
  ];

  config.blocks.requiredBlocks = config.blocks.requiredBlocks.filter(
    (item) => item !== 'eventMetadata',
  );

  //remove Lead Image block (using fieldimage block instead)
  delete config.blocks.blocksConfig.leadimage;

  // add group for saw blocks
  config.blocks.groupBlocksOrder.push({ id: 'saw', title: 'SAW Blocks' });

  config.blocks.blocksConfig.personMetadata = {
    id: 'personMetadata',
    title: 'Person Metadata',
    icon: descriptionSVG,
    group: 'saw',
    view: PersonMetadataView,
    blockSchema: PersonMetadataSchema,
    // restricted: ({ properties, block }) =>
    //   properties['@type'] === 'saw.persons.person' ? false : true,
    mostUsed: false,
    sidebarTab: 1,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  config.blocks.blocksConfig.contact = {
    id: 'contact',
    title: 'Contact',
    icon: emailSVG,
    group: 'saw',
    view: ContactView,
    // restricted: ({ properties, block }) =>
    //   properties['@type'] === 'saw.persons.person' ? false : true,
    mostUsed: false,
    sidebarTab: 0,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  config.blocks.blocksConfig.fieldImage = {
    id: 'fieldImage',
    title: 'Image from Field',
    icon: config.blocks.blocksConfig.image.icon,
    group: 'saw',
    view: FieldImageView,
    edit: FieldImageEdit,
    blockSchema: fieldImageBlockSchema,
    schemaEnhancer: composeSchema(
      defaultStylingSchema,
      standAloneFieldImageBlockSchemaEnhancer,
    ),
    dataAdapter: ImageBlockDataAdapter,
    mostUsed: false,
    sidebarTab: 0,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  config.blocks.blocksConfig.listing = {
    ...config.blocks.blocksConfig.listing,
    schemaEnhancer: composeSchema(
      defaultStylingSchema,
      blockWidthStylingSchema,
    ),
  };

  // Allow PersonMetadata and Projectgroup blocks in Columns block even if they are restricted
  config.blocks.blocksConfig.columnsBlock.schema.properties.allowedBlocks.items.choices =
    [
      ...config.blocks.blocksConfig.columnsBlock.schema.properties.allowedBlocks
        .items.choices,
      ['eventMetadata', 'EventMetadata'],
      ['personMetadata', 'Person Metadata'],
      ['projectgroup', 'Project Group'],
    ];

  config.blocks.blocksConfig.latestNews = {
    id: 'latestNews',
    title: 'latest news',
    icon: newsSVG,
    group: 'saw',
    view: LatestNewsView,
    blockSchema: LatestNewsSchema,
    schemaEnhancer: defaultStylingSchema,
    mostUsed: false,
    sidebarTab: 0,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  config.blocks.blocksConfig.projectgroup = {
    id: 'projectgroup',
    title: 'projectgroup',
    icon: groupSVG,
    group: 'saw',
    view: ProjectgroupView,
    edit: ProjectGroupEdit,
    blockSchema: projectGroupBlockSchema,
    // restricted: ({ properties, block }) =>
    //   properties['@type'] === 'saw.persons.project' ? false : true,
    mostUsed: false,
    sidebarTab: 0,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  const CONTENT_COLORS = [
    { name: 'pageheader-custom-color-1', label: 'Black' },
    { name: 'pageheader-custom-color-2', label: 'White' },
    { name: 'pageheader-custom-color-3', label: 'SAW green' },
  ];

  config.blocks.blocksConfig.slate = {
    ...config.blocks.blocksConfig.slate,
    view: TextBlockView,
  };

  config.blocks.blocksConfig.previewParagraph = {
    ...config.blocks.blocksConfig.slate,
    title: 'Preview Paragraph',
    id: 'previewParagraph',
    group: 'saw',
  };

  config.blocks.blocksConfig.imageGrid = {
    ...config.blocks.blocksConfig.gridBlock,
    title: 'image grid',
    id: 'imageGrid',
  };

  config.blocks.blocksConfig.eventMetadata = {
    ...config.blocks.blocksConfig.eventMetadata,
    view: EventMetadataView,
    edit: EventMetadataView,
    restricted: ({ properties, block }) => false,
  };

  config.blocks.blocksConfig.title = {
    ...config.blocks.blocksConfig.title,
    group: 'common',
    view: PageHeaderView,
    edit: PageHeaderEdit,
    blockHasOwnFocusManagement: true,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
    blockSchema: PageHeaderSchema,
    dataAdapter: PageHeaderBlockDataAdapter,
    textColors: CONTENT_COLORS,
    variations: [
      {
        id: 'onlyTitle',
        isDefault: true,
        title: 'only title',
        template: OnlyTitleBodyTemplate,
      },
      {
        id: 'flexible',
        isDefault: false,
        title: 'Custom Modifier',
        template: CustomImageBodyTemplate,
        schemaEnhancer: pageheaderSchemaEnhancer,
      },
      {
        id: 'person',
        isDefault: false,
        title: 'Person Header',
        template: PersonBodyTemplate,
      },
      {
        id: 'buffer-test',
        isDefault: false,
        title: '4 Test Header not showing up - always the 4th entry',
        template: ProjectBodyTemplate,
      },
      {
        id: 'project',
        isDefault: false,
        title: 'Project Header',
        template: ProjectBodyTemplate,
      },
      {
        id: 'event',
        isDefault: false,
        title: 'Event Header',
        template: EventHeaderTemplate,
      },
    ],
  };

  config.blocks.blocksConfig.teaser.variations = [
    ...config.blocks.blocksConfig.teaser.variations,
    {
      id: 'image-top-variation',
      title: 'SAW Fokusthemen',
      template: TeaserBlockSAW,
    },
  ];

  config.blocks.blocksConfig.image = {
    ...config.blocks.blocksConfig.image,
    schemaEnhancer: composeSchema(
      defaultStylingSchema,
      imageBlockSchemaEnhancerSAW,
      standAloneImageBlockSchemaEnhancer,
    ),
    dataAdapter: ImageBlockDataAdapter,
  };

  config.blocks.blocksConfig.gridBlock = {
    ...config.blocks.blocksConfig.gridBlock,
    maxLength: 5,
    themes: config.blocks.themes,
    view: gridView,
    schemaEnhancer: composeSchema(
      noGutterSchemaEnhancer,
      blockWidthStylingSchema,
    ),
    allowedBlocks: [
      ...config.blocks.blocksConfig.gridBlock.allowedBlocks,
      'fieldImage',
    ],
  };

  // config.blocks.blocksConfig.gridBlock.maxLength = 5;
  // config.blocks.blocksConfig.gridBlock.themes = config.blocks.themes;

  config.blocks.blocksConfig.gridBlock.blocksConfig.teaser.variations =
    config.blocks.blocksConfig.teaser.variations;

  config.blocks.blocksConfig.gridBlock.blocksConfig.image =
    config.blocks.blocksConfig.image;

  config.blocks.blocksConfig.gridBlock.blocksConfig.fieldImage =
    config.blocks.blocksConfig.fieldImage;

  config.blocks.blocksConfig.search.variations = [
    {
      id: 'facetsRightSide',
      title: 'Facets on right side',
      view: RightColumnFacets,
      isDefault: true,
    },
    {
      id: 'facetsLeftSide',
      title: 'Facets on left side',
      view: LeftColumnFacets,
      isDefault: false,
    },
    {
      id: 'facetsTopSide',
      title: 'Facets on top',
      view: TopSideFacets,
      isDefault: false,
    },
  ];

  config.blocks.blocksConfig.sponsors = {
    id: 'sponsors',
    initialValue: initialSponsorBlockData,
    title: 'sponsors',
    icon: paymentSVG,
    group: 'saw',
    view: SponsorView,
    edit: SponsorEdit,
    blockSchema: SponsorBlockSchema,
    schemaEnhancer: composeSchema(
      defaultStylingSchema,
      blockWidthStylingSchema,
    ),
    mostUsed: false,
    sidebarTab: 0,
    blocks: {},
    security: {
      addPermission: [],
      view: [],
    },
  };

  function initialSponsorBlockData({ id, value, formData, intl }) {
    return {
      ...formData.blocks[id],
      value: [
        {
          children: [
            {
              text: 'Dieses Forschungsvorhaben ist Teil im ',
            },
            {
              children: [
                {
                  text: 'Akademienprogramm',
                },
              ],
              data: {
                url: 'https://www.akademienunion.de/forschungsprojekte/',
              },
              type: 'link',
            },
            {
              text: ', das als derzeit größtes geistes- und kulturwissenschaftliches Langfrist-Forschungsprogramm der Bundesrepublik Deutschland von Bund und Ländern getragen wird. Diese Maßnahme wird mitfinanziert durch Steuermittel auf der Grundlage des vom Sächsischen Landtag beschlossenen Haushalts.',
            },
          ],
          type: 'p',
        },
      ],
    };
  }

  const personColumnsBlock = {
    '@type': 'columnsBlock',
    data: {
      blocks: {
        ini_column_one: {
          blocks: {
            initial_person_metadata: {
              '@type': 'personMetadata',
            },
            ini_next_slate: {
              '@type': 'slate',
              plaintext: '',
              styles: {},
              theme: 'default',
              value: [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                  type: 'p',
                },
              ],
            },
          },
          blocks_layout: {
            items: ['initial_person_metadata', 'ini_next_slate'],
          },
        },
        ini_column_two: {
          blocks: {
            initial_person_contact: {
              '@type': 'contact',
            },
            initial_person_portrait: {
              '@type': 'fieldImage',
              image: 'image',
            },
            initial_slate: {
              '@type': 'slate',
              plaintext: '',
              styles: {},
              theme: 'default',
              value: [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                  type: 'p',
                },
              ],
            },
          },
          blocks_layout: {
            items: [
              'initial_person_contact',
              'initial_person_portrait',
              'initial_slate',
            ],
          },
        },
      },
      blocks_layout: {
        items: ['ini_column_one', 'ini_column_two'],
      },
    },
    gridCols: ['twoThirds', 'oneThird'],
    gridSize: 12,
  };

  const projectColumnsBlock = {
    '@type': 'columnsBlock',
    data: {
      blocks: {
        ini_column_one: {
          blocks: {
            ini_next_slate: {
              '@type': 'slate',
              plaintext: '',
              styles: {},
              theme: 'default',
              value: [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                  type: 'p',
                },
              ],
            },
          },
          blocks_layout: {
            items: ['ini_next_slate'],
          },
        },
        ini_column_two: {
          blocks: {
            initial_person_contact: {
              '@type': 'contact',
            },
            initial_person_portrait: {
              '@type': 'fieldImage',
              image: 'image',
            },
            initial_slate: {
              '@type': 'slate',
              plaintext: '',
              styles: {},
              theme: 'default',
              value: [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                  type: 'p',
                },
              ],
            },
          },
          blocks_layout: {
            items: [
              'initial_person_contact',
              'initial_person_portrait',
              'initial_slate',
            ],
          },
        },
      },
      blocks_layout: {
        items: ['ini_column_one', 'ini_column_two'],
      },
    },
    gridCols: ['twoThirds', 'oneThird'],
    gridSize: 12,
  };

  const eventColumnsBlock = {
    '@type': 'columnsBlock',
    data: {
      blocks: {
        ini_column_one: {
          blocks: {
            ini_next_slate: {
              '@type': 'slate',
              plaintext: '',
              styles: {},
              theme: 'default',
              value: [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                  type: 'p',
                },
              ],
            },
          },
          blocks_layout: {
            items: ['ini_next_slate'],
          },
        },
        ini_column_two: {
          blocks: {
            initial_eventMetadata: {
              '@type': 'eventMetadata',
            },
            initial_image_field: {
              '@type': 'fieldImage',
              align: 'center',
              image: 'image',
              size: 'l',
              styles: {
                'blockWidth:noprefix': 'default',
                'size:noprefix': 'large',
              },
              theme: 'default',
            },
          },
          blocks_layout: {
            items: ['initial_eventMetadata', 'initial_image_field'],
          },
        },
      },
      blocks_layout: {
        items: ['ini_column_one', 'ini_column_two'],
      },
    },
    gridCols: ['twoThirds', 'oneThird'],
    gridSize: 12,
    reverseWrap: true,
  };

  config.blocks.initialBlocks['saw.persons.person'] = [
    {
      '@type': 'title',
      styles: {
        align: 'left',
        'blockWidth:noprefix': {
          '--block-width': 'var(--layout-container-width)',
        },
        textColor: 'pageheader-custom-color-1',
      },
      variation: 'person',
    },
    personColumnsBlock,
  ];

  config.blocks.initialBlocks['News Item'] = [
    {
      '@type': 'title',
      styles: {
        align: 'left',
        'blockWidth:noprefix': {
          '--block-width': 'var(--default-container-width)',
        },
        textColor: 'pageheader-custom-color-1',
      },
      variation: 'onlyTitle',
    },
    {
      '@type': 'fieldImage',
      align: 'right',
      image: 'image',
      size: 'm',
      styles: {
        'blockWidth:noprefix': 'default',
        'size:noprefix': 'medium',
      },
      theme: 'default',
    },
  ];

  config.blocks.initialBlocks['saw.persons.project'] = [
    {
      '@type': 'title',
      styles: {
        align: 'left',
        'blockWidth:noprefix': {
          '--block-width': 'var(--layout-container-width)',
        },
        textColor: 'pageheader-custom-color-1',
      },
      variation: 'project',
    },
    projectColumnsBlock,
  ];

  config.blocks.initialBlocks['Event'] = [
    {
      '@type': 'title',
      styles: {
        align: 'left',
        'blockWidth:noprefix': {
          '--block-width': 'var(--layout-container-width)',
        },
        textColor: 'pageheader-custom-color-1',
      },
      variation: 'event',
    },
    eventColumnsBlock,
  ];

  return config;
}
